// src/pages/Portfolio/Portfolio.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortfolio } from '../../hooks/usePortfolio';
import './Portfolio.css';

const Portfolio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if there's a state passed from navigation (from InviteWidget)
  const initialTab = location.state?.activeTab || 'deposit';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const {
    recentDeposits,
    recentWithdrawals,
    referralInfo,
    referralBonusSummary,
    referralWallet,
    fetchRecentTransactions,
    fetchReferralInfo,
    fetchReferralBonusSummary,
    fetchReferralWallet,
    copyReferralCode,
    copyReferralLink
  } = usePortfolio();

  // Ref to track if data has been fetched
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls in StrictMode
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    fetchRecentTransactions();
    fetchReferralInfo();
    fetchReferralBonusSummary();
    fetchReferralWallet();
  }, []);
  // Update active tab if location state changes
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const handleCopyCode = () => {
    if (copyReferralCode()) {
      alert('Referral code copied!');
    }
  };

  const handleCopyLink = () => {
    if (copyReferralLink()) {
      alert('Referral link copied!');
    }
  };

  const formatCurrency = (amount, method = null) => {
    // Use $ for crypto payments, ₦ for others
    const symbol = method === 'crypto' ? '$' : '₦';
    return `${symbol}${Number(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderDepositTab = () => (
    <div className="portfolio-tab-content">
      <div className="tab-header">
        <span className="tab-icon">⬇</span>
        <h2>Add Funds</h2>
      </div>
      <p className="tab-description">
        Add funds to your investment account through various payment methods.
      </p>
      
      <button 
        className="action-button primary"
        onClick={() => navigate('/deposit')}
      >
        <span className="button-icon">💳</span>
        Add Funds
      </button>

      <div className="recent-section">
        <h3>Recent Fundings</h3>
        {recentDeposits.length === 0 ? (
          <p className="empty-message">No recent fundings</p>
        ) : (
          <div className="recent-list">
            {recentDeposits.map((deposit) => (
              <div key={deposit.id} className="recent-item">
                <div className="recent-info">
                  <span className="recent-method">
                    {deposit.method?.replace('_', ' ') || 'Bank Transfer'}
                  </span>
                  <span className="recent-date">{formatDate(deposit.created_at)}</span>
                </div>
                <span className="recent-amount positive">
                  +{formatCurrency(deposit.display_amount || deposit.amount, deposit.method)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderWithdrawTab = () => (
    <div className="portfolio-tab-content">
      <div className="tab-header">
        <span className="tab-icon">⬆</span>
        <h2>Withdraw Funds</h2>
      </div>
      <p className="tab-description">
        Transfer your funds back to your bank account or crypto wallet.
      </p>
      
      <button 
        className="action-button primary"
        onClick={() => navigate('/withdraw')}
      >
        <span className="button-icon">💸</span>
        Make a Withdrawal
      </button>

      <div className="recent-section">
        <h3>Recent Withdrawals</h3>
        {recentWithdrawals.length === 0 ? (
          <p className="empty-message">No recent withdrawals</p>
        ) : (
          <div className="recent-list">
            {recentWithdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="recent-item">
                <div className="recent-info">
                  <span className="recent-method">
                    {withdrawal.method?.replace('_', ' ') || 'Bank Transfer'}
                  </span>
                  <span className="recent-date">{formatDate(withdrawal.created_at)}</span>
                </div>
                <span className="recent-amount negative">
                  -{formatCurrency(withdrawal.display_amount || withdrawal.amount, withdrawal.method)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderTradeTab = () => (
    <div className="portfolio-tab-content">
      <div className="tab-header">
        <span className="tab-icon">📈</span>
        <h2>Trade Stocks</h2>
      </div>
      <p className="tab-description">
        Buy and sell assets.
      </p>
      
      <button 
        className="action-button primary"
        onClick={() => navigate('/stocks')}
      >
        <span className="button-icon">💹</span>
        Start Trading
      </button>

      <div className="trade-info-cards">
        <div className="info-card">
          <div className="info-icon">🎯</div>
          <h4>Market Access</h4>
          <p>Trade assets listed</p>
        </div>
        <div className="info-card">
          <div className="info-icon">⚡</div>
          <h4>Real-time Trading</h4>
          <p>Execute trades instantly</p>
        </div>
        <div className="info-card">
          <div className="info-icon">📊</div>
          <h4>Portfolio Tracking</h4>
          <p>Monitor your investments</p>
        </div>
      </div>
    </div>
  );

  const renderInviteTab = () => (
    <div className="portfolio-tab-content">
      <div className="tab-header">
        <span className="tab-icon">👥</span>
        <h2>Invite Friends</h2>
      </div>
      <p className="tab-description">
        Invite your friends to join Trexar and earn rewards together.
      </p>
      
      {/* Referral Stats Cards */}
      <div className="referral-stats-card">
        <div className="referral-stat">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{referralInfo.totalReferrals}</div>
            <div className="stat-label">Total Referrals</div>
          </div>
        </div>
        <div className="referral-stat">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(referralBonusSummary?.total_bonuses_earned || 0)}</div>
            <div className="stat-label">Total Earned</div>
          </div>
        </div>
      </div>

      {/* Referral Wallet Balance */}
      <div className="referral-wallet-section">
        <h3>Your Referral Wallet</h3>
        <div className="wallet-balance-cards">
          <div className="wallet-balance-card available">
            <div className="balance-icon">✅</div>
            <div className="balance-content">
              <div className="balance-label">Available</div>
              <div className="balance-amount">
                {formatCurrency(referralWallet?.available_balance || 0)}
              </div>
              <div className="balance-description">Ready to use for trading</div>
            </div>
          </div>
          <div className="wallet-balance-card locked">
            <div className="balance-icon">🔒</div>
            <div className="balance-content">
              <div className="balance-label">Locked</div>
              <div className="balance-amount">
                {formatCurrency(referralWallet?.locked_balance || 0)}
              </div>
              <div className="balance-description">
                Complete {referralBonusSummary?.required_trades || 12} trades to unlock
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar for Trade Requirements */}
        {referralBonusSummary?.locked_bonuses > 0 && (
          <div className="trade-progress-section">
            <h4>Unlock Progress</h4>
            <div className="progress-bar-container">
              <div 
                className="progress-bar"
                style={{
                  width: `${Math.min(
                    ((referralBonusSummary.completed_trades || 0) / (referralBonusSummary.required_trades || 12)) * 100, 
                    100
                  )}%`
                }}
              ></div>
            </div>
            <div className="trade-count-label">
              {Math.min(referralBonusSummary.completed_trades || 0, referralBonusSummary.required_trades || 12)} of {referralBonusSummary.required_trades || 12} trades completed
            </div>
            <div className="progress-subtext">
              Trade volume: {formatCurrency(referralBonusSummary.completed_trade_volume || 0)} / {formatCurrency(referralBonusSummary.required_trade_volume || 0)}
            </div>
          </div>
        )}
      </div>

      {/* Referral Code Section */}
      <div className="referral-code-section">
        <label className="referral-label">Your Referral Code</label>
        <div className="referral-input-group">
          <input 
            type="text" 
            value={referralInfo.referralCode} 
            readOnly 
            className="referral-input"
          />
          <button 
            className="referral-copy-btn"
            onClick={handleCopyCode}
            title="Copy referral code"
          >
            📋
          </button>
        </div>
      </div>

      <div className="referral-code-section">
        <label className="referral-label">Your Referral Link</label>
        <div className="referral-input-group">
          <input 
            type="text" 
            value={referralInfo.referralLink} 
            readOnly 
            className="referral-input"
          />
          <button 
            className="referral-copy-btn"
            onClick={handleCopyLink}
            title="Copy referral link"
          >
            📋
          </button>
        </div>
      </div>

      <div className="referral-benefits">
        <h3>Referral Benefits</h3>
        <div className="benefits-list">
          <div className="benefit-item">
            <div className="benefit-number">1</div>
            <div className="benefit-content">
              <h4>10% funding Bonus</h4>
              <p>Earn 10% of your friend's first funding amount to your main wallet</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-number">2</div>
            <div className="benefit-content">
              <h4>15% Friend Bonus (Level 4+)</h4>
              <p>Your friend gets 15% bonus on first funding when you're Level 4+</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-number">3</div>
            <div className="benefit-content">
              <h4>Trade to Unlock</h4>
              <p>Friends complete 12 trades to unlock their 15% bonus</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <div className="header-content">
          <div className="header-welcome">
            <h1>Welcome back!</h1>
            <p>Track and manage your investments</p>
          </div>
        </div>
      </div>

      <div className="portfolio-container">
        <div className="portfolio-title-section">
          <h2 className="portfolio-title">Portfolio</h2>
          <p className="portfolio-subtitle">Manage your investments and transactions</p>
          <button 
            className="view-history-button"
            onClick={() => navigate('/transaction-history')}
          >
            <span className="history-icon">📊</span>
            View History
          </button>
        </div>

        <div className="portfolio-tabs">
          <button
            className={`portfolio-tab ${activeTab === 'deposit' ? 'active' : ''}`}
            onClick={() => setActiveTab('deposit')}
          >
            <span className="portfolio-tab-icon">⬇</span>
            <span>Funding</span>
          </button>
          <button
            className={`portfolio-tab ${activeTab === 'withdraw' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdraw')}
          >
            <span className="portfolio-tab-icon">⬆</span>
            <span>Withdraw</span>
          </button>
          <button
            className={`portfolio-tab ${activeTab === 'trade' ? 'active' : ''}`}
            onClick={() => setActiveTab('trade')}
          >
            <span className="portfolio-tab-icon">📈</span>
            <span>Trade</span>
          </button>
          <button
            className={`portfolio-tab ${activeTab === 'invite' ? 'active' : ''}`}
            onClick={() => setActiveTab('invite')}
          >
            <span className="portfolio-tab-icon">👥</span>
            <span>Invite</span>
          </button>
        </div>

        <div className="portfolio-content">
          {activeTab === 'deposit' && renderDepositTab()}
          {activeTab === 'withdraw' && renderWithdrawTab()}
          {activeTab === 'trade' && renderTradeTab()}
          {activeTab === 'invite' && renderInviteTab()}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;